import React, {forwardRef, useImperativeHandle} from 'react';
import Flatpickr from 'react-flatpickr';
import {setDateFormat} from '../helpers/dateUtils';
import {TextField} from '../TextField';
import type {BaseDatePickerProps, BaseDatePickerRef} from './BaseDatePicker';
import {BaseDatePicker} from './BaseDatePicker';
import {getMonthGap} from '../helpers/getMonthGap';
import {useControlled} from '@mui/material';
import {DateRangeInputs, DateRangeInputsProps} from './DateRangeInputs';

export interface DateRangePickerProps extends Omit<BaseDatePickerProps, 'render' | 'mode' | 'enableTime'> {
    /**
      * IDs are automatically generated by component for accessibility purposes.
      * Use this prop to pass a specific ID value that will be appended.
      * @default "";
      *
      */
    id?: DateRangeInputsProps['id'];
    /**
     * Start Date Placeholder text to the DateRangePicker.
     * @default 'Start Date'
     */
    startDatePlaceholder?: DateRangeInputsProps['startDatePlaceholder'];
    /**
     * End Date Placeholder text to the DateRangePicker.
     * @default 'End Date'
     */
    endDatePlaceholder?: DateRangeInputsProps['endDatePlaceholder'];
    /**
     * Type currently defaults to TextFieldProps. However, if you change
     * the `renderInput`, we suggest to also update the type parameter.
     * For example, we use TextFieldProps because the renderInput function returns a TextField.
     * @default TextFieldProps
     */
    StartInputProps?: DateRangeInputsProps['StartInputProps'];
    /**
     * Type currently defaults to TextFieldProps. However, if you change
     * the `renderInput`, we suggest to also update the type parameter.
     * For example, we use TextFieldProps because the renderInput function returns a TextField.
     * @default TextFieldProps
     */
    EndInputProps?: DateRangeInputsProps['EndInputProps'];
    /**
      * The default React Input Component used to render the input.
      * @default TextField
      */
    InputComponent?: DateRangeInputsProps['InputComponent'];
}

export const DateRangePicker = forwardRef<BaseDatePickerRef, DateRangePickerProps>(({
    dualMonth, InputComponent = TextField, StartInputProps, EndInputProps,
    defaultValue, dateFormat: userDateFormat, size = 'medium', id = '', value, onChange, ...props
}, ref) => {
    const fpRef = React.useRef<Flatpickr['flatpickr']>(null);

    useImperativeHandle<BaseDatePickerRef, BaseDatePickerRef>(ref, () => fpRef.current);

    const dateFormat = userDateFormat || setDateFormat(false, props.noCalendar);

    /** Normalize the values of the value and defaultValue props so we can use them with useControlled */
    const startPropsValue = React.useMemo(() => Array.isArray(value) ? value[0] || '' : value, [value]);
    const startDefault = React.useMemo(() => Array.isArray(defaultValue) ? defaultValue[0] : defaultValue, [defaultValue]);
    const endPropsValue = React.useMemo(() => Array.isArray(value) ? value[1] || '' : value, [value]);
    const endDefault = React.useMemo(() => Array.isArray(defaultValue) ? defaultValue[1] : defaultValue, [defaultValue]);

    /** MUI's useControlled manages the value and defaultValue so consumers can use this component similar to a react input */
    const [startValue, setStartValue] = useControlled({
        controlled: startPropsValue,
        default: startDefault,
        name: 'startInputValue'
    });
    const [endValue, setEndValue] = useControlled({
        controlled: endPropsValue,
        default: endDefault,
        name: 'endInputValue'
    });

    const renderComponent: BaseDatePickerProps['render'] = React.useCallback((_, renderRef): React.ReactElement => (
        <DateRangeInputs
            EndInputProps={EndInputProps}
            InputComponent={InputComponent}
            StartInputProps={StartInputProps}
            dateFormat={dateFormat}
            endDatePlaceholder={props.endDatePlaceholder}
            endValue={endValue}
            fpRef={fpRef}
            id={id}
            renderRef={renderRef}
            size={size}
            startDatePlaceholder={props.startDatePlaceholder}
            startValue={startValue}
        />
    ), [
        EndInputProps,
        StartInputProps,
        InputComponent,
        props.endDatePlaceholder,
        props.startDatePlaceholder,
        size,
        id,
        startValue,
        endValue,
        fpRef,
        dateFormat
    ]);

    return (
        <BaseDatePicker
            {...props}
            dualMonth={dualMonth}
            enableTime={false}
            mode='range'
            noCalendar={false}
            onChange={(dates, ...args): void => {
                setStartValue(dates[0]);
                setEndValue(dates[1]);
                onChange?.(dates, ...args);
            }}
            onOpen={(dates, ...args): void => {
                props.onOpen?.(dates, ...args);

                /** NOTE: Workaround because flatpickr changes month */
                if (dualMonth && dates[0] && dates[1]) {
                    const monthGap = getMonthGap(dates[0], dates[1]);

                    // monthGap is 1, e.g. Aug --> Sep
                    if (monthGap === 1) {
                        fpRef.current?.changeMonth(dates[0].getMonth(), false);
                        return;
                    }

                    // monthGap is more than 1, e.g. Aug --> Jan
                    if (monthGap > 1) {
                        fpRef.current?.changeMonth(dates[1].getMonth() - 1, false);
                    }
                }
            }}
            ref={fpRef}
            render={renderComponent}
            size={size}
            value={[startValue, endValue]}
        />
    );
});

DateRangePicker.displayName = 'DateRangePicker';
